/** @jsx jsx */
import React, { useCallback } from 'react';
import { styled, jsx } from '@compiled/react';
import noop from 'lodash/noop';
import DropdownMenu, { DropdownItemGroup } from '@atlaskit/dropdown-menu';
import type { DropdownMenuProps, OnOpenChangeArgs } from '@atlaskit/dropdown-menu/types';
import { gridSize } from '@atlassian/jira-common-styles/src/main.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import { useIntl } from '@atlassian/jira-intl';
import type { UploadPermissions } from '@atlassian/jira-issue-navigator-issue-operations-services/src/services/media-upload-permissions/types.tsx';
import type {
	DataState,
	Operation,
	IssueLink,
} from '@atlassian/jira-issue-navigator-issue-operations-services/src/services/operations-and-actions/index.tsx';
import {
	APPROVE_APPROVAL,
	REJECT_APPROVAL,
	SKIP_FIELDS_OLD,
	SKIP_FIELDS_NEW,
	URL_OVERRIDE_FIELDS,
	DIALOG_FIELDS,
	OPERATION_IDS,
} from '@atlassian/jira-issue-operations-commons/src/constants.tsx';
import { fireUIAnalytics, useAnalyticsEvents } from '@atlassian/jira-product-analytics-bridge';
import { ErrorState } from '../../common/ui/error-state/index.tsx';
import { DropdownButtonItem } from './button-item/index.tsx';
import messages from './messages.tsx';
import { RouteLinkItem, RouteLinkItemOld } from './route-link-item/index.tsx';

export type Props = {
	issueId: number;
	issueKey: string;
	workType?: string | null;
	dataState: DataState;
	uploadPermissions: UploadPermissions;
	onIssueRefetch: () => void;
	onIssueDelete: () => void;
	onRankToTop: (onSuccess: () => void, onFailure: () => void) => void;
	onRankToBottom: (onSuccess: () => void, onFailure: () => void) => void;
	onApprove: (onSuccess: () => void, onFailure: () => void) => void;
	onReject: (onSuccess: () => void, onFailure: () => void) => void;
	elementToFocusOnClose?: string;
	isCreateSubTaskAllowed?: boolean;
};

const isUrlOverrideFieldClass = (
	className: string,
): className is keyof typeof URL_OVERRIDE_FIELDS =>
	Object.keys(URL_OVERRIDE_FIELDS).includes(className);

const getDialogFields = () => [...DIALOG_FIELDS, APPROVE_APPROVAL, REJECT_APPROVAL];

const mapOperationToActionItem = ({ name, url: givenUrl, styleClass: field }: Operation) => {
	const url = isUrlOverrideFieldClass(field) ? URL_OVERRIDE_FIELDS[field] : givenUrl;

	return {
		name,
		url,
		field,
	};
};

const linksNeedToSkip = ({ id }: IssueLink) =>
	fg('nin_add_link_issue_to_issue_operations')
		? !SKIP_FIELDS_NEW.includes(id)
		: !SKIP_FIELDS_OLD.includes(id);

export const DropdownMenuGroupNew = ({
	issueId,
	dataState,
	uploadPermissions,
	isCreateSubTaskAllowed,
	workType,
	...props
}: Props) => {
	const { issueKey } = props;
	const { data, loadFieldResources } = dataState;
	const { permissions, error: permissionsError } = uploadPermissions;
	const { formatMessage } = useIntl();

	const { createAnalyticsEvent } = useAnalyticsEvents();

	const fireAnalyticsEvent = useCallback(
		(fieldType: string) => {
			const analyticsEvent = createAnalyticsEvent({
				action: 'clicked',
				actionSubject: 'field',
			});
			fireUIAnalytics(analyticsEvent, 'meatballField', {
				fieldType,
			});
		},
		[createAnalyticsEvent],
	);

	const skipFields = fg('nin_add_link_issue_to_issue_operations')
		? SKIP_FIELDS_NEW
		: SKIP_FIELDS_OLD;

	const operationsNeedToSkip = useCallback(
		({ styleClass: field }: Operation) =>
			!skipFields.includes(field) &&
			!(field === OPERATION_IDS.ATTACH_FILE && permissionsError) &&
			!(
				field === OPERATION_IDS.CREATE_SUBTASK &&
				!isCreateSubTaskAllowed &&
				fg('jsc_m2_hierarchy_fe_changes')
			),
		[isCreateSubTaskAllowed, permissionsError, skipFields],
	);

	const isIssueArchivalEnabled =
		fg('issue_deletion_two_step_process_free_standard') &&
		data?.operations
			?.filter(operationsNeedToSkip)
			.map(mapOperationToActionItem)
			.some(({ field }) => field !== null && field === OPERATION_IDS.ARCHIVE);

	const createButtonItem = (name: string, url: string, field: string) => (
		<ListItem key={field}>
			<DropdownButtonItem
				issueId={issueId}
				url={url}
				workType={workType}
				name={name}
				styleClass={field}
				onAddFieldClick={loadFieldResources}
				uploadPermissions={permissions}
				onFireAnalyticsEvent={fireAnalyticsEvent}
				isIssueArchivalEnabled={isIssueArchivalEnabled}
				onAddFlag={noop}
				{...props}
			/>
		</ListItem>
	);

	return (
		<DropdownMenuHeight>
			<DropdownItemGroup testId="issue-navigator-issue-operations.ui.dropdown-menu-group.menu-container">
				<List>
					<ListItem>
						<RouteLinkItem
							key={OPERATION_IDS.VIEW_ISSUE}
							href={`/browse/${issueKey}`}
							onClick={() => fireAnalyticsEvent(OPERATION_IDS.VIEW_ISSUE)}
							params={{ issueKey }}
							menuItemId={OPERATION_IDS.VIEW_ISSUE}
							actionSubjectId="issueOperationsMeatballMenu"
						>
							{formatMessage(
								fg('jira-issue-terminology-update-m4')
									? messages.viewIssueLabelIssueTerminologyRefreshM4
									: messages.viewIssueLabel,
							)}
						</RouteLinkItem>
					</ListItem>

					{data?.operations
						?.filter(operationsNeedToSkip)
						.map(mapOperationToActionItem)
						.map(({ name, url, field }) => {
							const dialogFields = getDialogFields();
							if (dialogFields.includes(field)) {
								return createButtonItem(name, url, field);
							}

							return fg('nin_trigger_connect_webitem_dialog') ? (
								<ListItem key={field}>
									<RouteLinkItem
										href={url}
										onClick={() => fireAnalyticsEvent(field)}
										styleClass={field}
										name={name}
										menuItemId={name}
										actionSubjectId="issueOperationsMeatballMenu"
									>
										{name}
									</RouteLinkItem>
								</ListItem>
							) : (
								<ListItem key={field}>
									<RouteLinkItemOld
										href={url}
										onClick={() => fireAnalyticsEvent(field)}
										styleClass={field}
										name={name}
									>
										{name}
									</RouteLinkItemOld>
								</ListItem>
							);
						})}

					{data?.links
						?.filter(linksNeedToSkip)
						.map(({ id, url, label }) => createButtonItem(label, url, id))}
				</List>
			</DropdownItemGroup>
		</DropdownMenuHeight>
	);
};

export type PropsOld = DropdownMenuProps & {
	issueId: number;
	issueKey: string;
	workType?: string | null;
	dataState: DataState;
	uploadPermissions: UploadPermissions;
	onIssueRefetch: () => void;
	onIssueDelete: () => void;
	onClose: () => void;
	onOpenChange: (args: OnOpenChangeArgs) => void;
	onAddFlag: (styleClass: string, isSuccessful: boolean) => void;
	onRetry: () => void;
	onRankToTop: (onSuccess: () => void, onFailure: () => void) => void;
	onRankToBottom: (onSuccess: () => void, onFailure: () => void) => void;
	onApprove: (onSuccess: () => void, onFailure: () => void) => void;
	onReject: (onSuccess: () => void, onFailure: () => void) => void;
	testId?: string;
	elementToFocusOnClose?: string;
	isCreateSubTaskAllowed?: boolean;
};

export const DropdownMenuGroupOld = ({
	issueId,
	isOpen,
	placement,
	trigger,
	dataState,
	onClose,
	onOpenChange,
	onRetry,
	uploadPermissions,
	testId,
	isCreateSubTaskAllowed,
	workType,
	...props
}: PropsOld) => {
	const { issueKey } = props;
	const { data, error, loading: operationsLoading, loadFieldResources } = dataState;
	const { permissions, loading: permissionsLoading, error: permissionsError } = uploadPermissions;
	const { formatMessage } = useIntl();

	const { createAnalyticsEvent } = useAnalyticsEvents();

	const fireAnalyticsEvent = useCallback(
		(fieldType: string) => {
			const analyticsEvent = createAnalyticsEvent({
				action: 'clicked',
				actionSubject: 'field',
			});
			fireUIAnalytics(analyticsEvent, 'meatballField', {
				fieldType,
			});
		},
		[createAnalyticsEvent],
	);

	const skipFields = fg('nin_add_link_issue_to_issue_operations')
		? SKIP_FIELDS_NEW
		: SKIP_FIELDS_OLD;

	const operationsNeedToSkip = useCallback(
		({ styleClass: field }: Operation) =>
			!skipFields.includes(field) &&
			!(field === OPERATION_IDS.ATTACH_FILE && permissionsError) &&
			!(
				field === OPERATION_IDS.CREATE_SUBTASK &&
				!isCreateSubTaskAllowed &&
				fg('jsc_m2_hierarchy_fe_changes')
			),
		[isCreateSubTaskAllowed, permissionsError, skipFields],
	);

	const isIssueArchivalEnabled =
		fg('issue_deletion_two_step_process_free_standard') &&
		data?.operations
			?.filter(operationsNeedToSkip)
			.map(mapOperationToActionItem)
			.some(({ field }) => field !== null && field === OPERATION_IDS.ARCHIVE);

	const createButtonItem = useCallback(
		(name: string, url: string, field: string) => (
			<ListItem key={field}>
				<DropdownButtonItem
					issueId={issueId}
					url={url}
					workType={workType}
					name={name}
					styleClass={field}
					onAddFieldClick={loadFieldResources}
					onClose={onClose}
					uploadPermissions={permissions}
					onFireAnalyticsEvent={fireAnalyticsEvent}
					isIssueArchivalEnabled={isIssueArchivalEnabled}
					{...props}
				/>
			</ListItem>
		),
		[
			fireAnalyticsEvent,
			isIssueArchivalEnabled,
			issueId,
			loadFieldResources,
			onClose,
			permissions,
			props,
			workType,
		],
	);

	const createLinkItemNew = useCallback(
		(name: string, url: string, field: string) => (
			<ListItem key={field}>
				<RouteLinkItem
					href={url}
					onClick={() => fireAnalyticsEvent(field)}
					styleClass={field}
					name={name}
					menuItemId={name}
					actionSubjectId="issueOperationsMeatballMenu"
				>
					{name}
				</RouteLinkItem>
			</ListItem>
		),
		[fireAnalyticsEvent],
	);

	const createLinkItemOld = useCallback(
		(name: string, url: string, field: string) => (
			<ListItem key={field}>
				<RouteLinkItemOld
					href={url}
					onClick={() => fireAnalyticsEvent(field)}
					styleClass={field}
					name={name}
				>
					{name}
				</RouteLinkItemOld>
			</ListItem>
		),
		[fireAnalyticsEvent],
	);

	const dropdownMenuProps: DropdownMenuProps = {
		isOpen,
		placement: 'bottom-end',
		shouldRenderToParent: true,
		trigger,
		onOpenChange,
		testId,
		isLoading: operationsLoading || permissionsLoading,
	};

	if (error ?? false) {
		return (
			<DropdownMenu
				{...dropdownMenuProps}
				shouldRenderToParent={fg('should-render-to-parent-should-be-true-empanada')}
			>
				<ErrorState onRetry={onRetry} />
			</DropdownMenu>
		);
	}

	return (
		<DropdownMenu
			{...dropdownMenuProps}
			shouldRenderToParent={fg('should-render-to-parent-should-be-true-empanada')}
		>
			<DropdownMenuHeight>
				<DropdownItemGroup testId="issue-navigator-issue-operations.ui.dropdown-menu-group.menu-container">
					<List>
						<ListItem>
							<RouteLinkItem
								key={OPERATION_IDS.VIEW_ISSUE}
								href={`/browse/${issueKey}`}
								onClick={() => fireAnalyticsEvent(OPERATION_IDS.VIEW_ISSUE)}
								params={{ issueKey }}
								menuItemId={OPERATION_IDS.VIEW_ISSUE}
								actionSubjectId="issueOperationsMeatballMenu"
							>
								{formatMessage(
									fg('jira-issue-terminology-update-m4')
										? messages.viewIssueLabelIssueTerminologyRefreshM4
										: messages.viewIssueLabel,
								)}
							</RouteLinkItem>
						</ListItem>

						{data?.operations
							?.filter(operationsNeedToSkip)
							.map(mapOperationToActionItem)
							.map(({ name, url, field }) => {
								const dialogFields = getDialogFields();
								if (dialogFields.includes(field)) {
									return createButtonItem(name, url, field);
								}

								return fg('nin_trigger_connect_webitem_dialog')
									? createLinkItemNew(name, url, field)
									: createLinkItemOld(name, url, field);
							})}

						{data?.links
							?.filter(linksNeedToSkip)
							.map(({ id, url, label }) => createButtonItem(label, url, id))}
					</List>
				</DropdownItemGroup>
			</DropdownMenuHeight>
		</DropdownMenu>
	);
};

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const List = styled.ul({
	padding: '0',
	margin: '0',
	listStyle: 'none',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const ListItem = styled.li({
	padding: '0',
	margin: '0',
	listStyle: 'none',
});

/**
 * Work around for https://product-fabric.atlassian.net/browse/DSP-7932
 */

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const DropdownMenuHeight = styled.div({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	maxHeight: gridSize * 39,
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	minWidth: gridSize * 30,
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	maxWidth: gridSize * 30,
});
